import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Menu, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api } from "../lib/api";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/deals", label: "Deals", testid: "nav-deals" },
  { to: "/trending", label: "Trending", testid: "nav-trending" },
  { to: "/blog", label: "Blog", testid: "nav-blog" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export const Header = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      setMobileOpen(false);
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-navy-700 bg-navy-900/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" aria-label="Open menu" className="rounded-lg p-2 text-gold-400 hover:bg-navy-800 md:hidden">
              <Menu size={22} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto bg-white">
            <SheetHeader>
              <SheetTitle className="text-left">
                <img src="/logo-wide.png" alt="ShoppinMagic" className="h-10 w-auto" />
              </SheetTitle>
            </SheetHeader>
            <form onSubmit={submitSearch} className="mt-4">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products…"
                data-testid="mobile-search-input"
                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:outline-none"
              />
            </form>
            <nav className="mt-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-100"
                >
                  {l.label}
                </Link>
              ))}
              <p className="mt-4 px-3 text-xs font-bold uppercase tracking-wider text-slate-400">Categories</p>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  data-testid={`mobile-nav-category-${c.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                >
                  {c.name}
                </Link>
              ))}
              <Link
                to="/sell"
                data-testid="mobile-nav-sell"
                onClick={() => setMobileOpen(false)}
                className="mt-4 rounded-xl bg-gold-600 px-3 py-2.5 text-center text-sm font-bold text-white"
              >
                Sell on ShoppinMagic
              </Link>
            </nav>
          </SheetContent>
        </Sheet>

        <Link to="/" data-testid="logo-link" className="flex shrink-0 items-center">
          <img src="/logo-wide.png" alt="Shoppin Magic — affiliate marketplace" className="h-10 w-auto sm:h-12" />
        </Link>

        <form onSubmit={submitSearch} className="hidden flex-1 md:flex" role="search">
          <div className="relative w-full max-w-xl">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, brands, and deals…"
              data-testid="search-input"
              className="w-full rounded-full border border-navy-600 bg-navy-800 text-white placeholder:text-navy-300 py-2.5 pl-4 pr-12 text-sm focus:border-gold-500 focus:bg-navy-800 focus:outline-none focus:ring-2 focus:ring-gold-500"
            />
            <button
              type="submit"
              aria-label="Search"
              data-testid="search-submit-button"
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-gold-600 p-2 text-white hover:bg-gold-700"
            >
              <Search size={16} />
            </button>
          </div>
        </form>

        <nav className="ml-auto hidden items-center gap-1 md:flex" aria-label="Main navigation">
          <Link to="/" data-testid="nav-home" className="rounded-lg px-3 py-2 text-sm font-semibold text-white/85 hover:bg-navy-800 hover:text-white">
            Home
          </Link>
          <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
            <button
              data-testid="nav-categories"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-white/85 hover:bg-navy-800 hover:text-white"
            >
              Categories <ChevronDown size={14} className={`transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div data-testid="categories-dropdown" className="absolute left-0 top-full w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    data-testid={`nav-category-${c.slug}`}
                    onClick={() => setCatOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-gold-50 hover:text-gold-700"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {NAV_LINKS.slice(1).map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className="rounded-lg px-3 py-2 text-sm font-semibold text-white/85 hover:bg-navy-800 hover:text-white">
              {l.label}
            </Link>
          ))}
          <Link
            to="/sell"
            data-testid="nav-sell"
            className="ml-2 rounded-full bg-gold-500 px-4 py-2 text-sm font-bold text-navy-900 transition-colors hover:bg-gold-400"
          >
            Sell With Us
          </Link>
        </nav>
      </div>
    </header>
  );
};
